import { Button } from "@shared/components/ui/Button";
import { getProfileImage } from "@shared/hooks/useProfile";
import { useProfanityStore } from "@shared/services/profanity.service";
import { UserProfile } from "@shared/types/user";
import { containsProfanity } from "@shared/utils/profanityFilter";
import theme from "@shared/utils/themeColors";
import React, { useEffect, useRef, useState } from "react";
import { SpaceComment } from "../types/space.types";

interface CommentFormProps {
  profile: Partial<UserProfile>;
  commentContent: string;
  setCommentContent: (content: string) => void;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
  parentComment?: SpaceComment;
  onCancel?: () => void;
  availableUsers?: Partial<UserProfile>[];
  compact?: boolean;
}

/**
 * Reusable comment form component that can be used for both new comments and replies
 */
const CommentForm: React.FC<CommentFormProps> = ({
  profile,
  commentContent,
  setCommentContent,
  onSubmit,
  isSubmitting,
  parentComment,
  onCancel,
  availableUsers = [],
  compact = false,
}) => {
  const { openProfanityModal } = useProfanityStore();
  const [mentionQuery, setMentionQuery] = useState<string>("");
  const [showMentionSuggestions, setShowMentionSuggestions] =
    useState<boolean>(false);
  const [mentionSuggestions, setMentionSuggestions] = useState<
    Partial<UserProfile>[]
  >([]);
  const [cursorPosition, setCursorPosition] = useState<number>(0);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Handle text change and detect @ mentions
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setCommentContent(newValue);

    // Store cursor position for mention insertion
    setCursorPosition(e.target.selectionStart || 0);

    // Check if we're in a mention context (after @)
    const textBeforeCursor = newValue.substring(
      0,
      e.target.selectionStart || 0,
    );
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      const query = mentionMatch[1].toLowerCase();
      setMentionQuery(query);

      // Filter available users based on query
      const filtered = availableUsers.filter((user) =>
        user.name?.toLowerCase().includes(query),
      );

      setMentionSuggestions(filtered);
      setShowMentionSuggestions(true);
    } else {
      setShowMentionSuggestions(false);
    }
  };

  // Insert mention at cursor position
  const insertMention = (username: string) => {
    if (!textareaRef.current) return;

    const textBeforeCursor = commentContent.substring(0, cursorPosition);
    const textAfterCursor = commentContent.substring(cursorPosition);

    // Find the position of the @ character before cursor
    const lastAtPos = textBeforeCursor.lastIndexOf("@");
    if (lastAtPos === -1) return;

    // Replace the @query with @username
    const newText =
      textBeforeCursor.substring(0, lastAtPos) +
      `@${username} ` +
      textAfterCursor;

    setCommentContent(newText);
    setShowMentionSuggestions(false);

    // Focus back on textarea and set cursor position after the inserted mention
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newCursorPos = lastAtPos + username.length + 2; // +2 for @ and space
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  // Handle keyboard navigation in mention suggestions
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showMentionSuggestions) return;

    if (e.key === "Escape") {
      setShowMentionSuggestions(false);
      e.preventDefault();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      // Could implement keyboard navigation through suggestions
      e.preventDefault();
    } else if (
      e.key === "Enter" &&
      showMentionSuggestions &&
      mentionSuggestions.length > 0
    ) {
      insertMention(mentionSuggestions[0].name || "");
      e.preventDefault();
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(event.target as Node)
      ) {
        setShowMentionSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentContent.trim()) return;
    if (containsProfanity(commentContent)) {
      openProfanityModal();
      return;
    }
    await onSubmit();
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`flex items-start ${compact ? "space-x-2" : "space-x-3"}`}
    >
      <img
        className={`rounded-full shrink-0 ${compact ? "h-8 w-8" : "h-10 w-10"}`}
        src={getProfileImage(profile)}
        alt="User"
      />
      <div className="grow relative">
        <label
          htmlFor={`comment-form-${parentComment?.id || "main"}`}
          className="sr-only"
        >
          Write a comment
        </label>
        <textarea
          id={`comment-form-${parentComment?.id || "main"}`}
          ref={textareaRef}
          className={`form-textarea w-full ${theme.border.focus}`}
          rows={compact ? 2 : 4}
          placeholder={parentComment ? "Write a reply…" : "Write a comment…"}
          value={commentContent}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
        />

        {/* Mention suggestions dropdown */}
        {/* {showMentionSuggestions && mentionSuggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            className={`absolute z-10 mt-1 w-64 ${theme.bg.primary} ${theme.shadow.lg} rounded-md border ${theme.border.primary} max-h-48 overflow-y-auto`}
          >
            <ul className="py-1">
              {mentionSuggestions.map((user) => (
                <li
                  key={user.id}
                  className={`px-3 py-2 ${theme.bg.hover} cursor-pointer flex items-center`}
                  onClick={() => insertMention(user.name || '')}
                >
                  <img
                    src={user.imageUrl || UserAvatar}
                    alt={user.name || "User"}
                    className="w-6 h-6 rounded-full mr-2"
                  />
                  <span className="text-sm font-medium">{user.name}</span>
                </li>
              ))}
            </ul>
          </div>
        )} */}

        <div className="flex justify-between mt-3">
          <div>
            {parentComment && onCancel && (
              <div className={`text-xs ${theme.text.muted}`}>
                Replying to {parentComment.author.name}{" "}
                <button
                  type="button"
                  className={`${theme.text.brand} ${theme.text.brandHover}`}
                  onClick={onCancel}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <Button
            type="submit"
            variant="primary"
            size={compact ? "sm" : "md"}
            disabled={!commentContent.trim() || isSubmitting}
            isLoading={isSubmitting}
            loadingText={
              parentComment ? "Posting reply..." : "Posting comment..."
            }
          >
            {parentComment ? "Post Reply" : "Post Comment"}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CommentForm;
